import type { IAgreement } from '@/models/agreement'
import { Agreement } from '@/models/agreement'
import { SystemError } from '@/models/error'
import agreementService from '@/services/agreement/agreement'
import { defineStore } from 'pinia'
import type { IAgreementState } from './agreement.types'

export const useAgreementStore = defineStore('agreement', {
  state: (): IAgreementState => ({
    agreement: null,
    loading: false
  }),

  getters: {
    // Hydrates a strict Agreement model. Returns null when this deployment
    // has no active agreement (presence-driven — no brand key involved).
    // Fails closed to null on any partial/malformed payload rather than
    // throwing, since this getter is the pure presence check SD-19-05 relies on.
    getAgreement(state: IAgreementState): IAgreement | null {
      if (!state.agreement || !state.agreement.version || !state.agreement.title || !state.agreement.content) {
        return null
      }

      try {
        return new Agreement(state.agreement)
      } catch {
        return null
      }
    }
  },

  actions: {
    async fetchActiveAgreement() {
      this.loading = true
      try {
        this.agreement = await agreementService.fetchActiveAgreement()
      } finally {
        this.loading = false
      }
    },

    async recordAcceptance(version: string) {
      try {
        await agreementService.recordAcceptance(version)
      } catch (error) {
        // 409 = the active agreement changed since it was displayed (the
        // backend binds acceptance to the shown version and rejects stale
        // submissions). Re-fetch the current version so the user reviews and
        // accepts the terms actually in force rather than recording acceptance
        // of stale text; re-throw so the wizard keeps them on the gate.
        //
        // HTTP errors reach here as a normalized `SystemError` (axios
        // interceptor, src/api/axios.ts) — inspect `statusCode`, NOT
        // `error.response.status`, matching the repo pattern (MicrosoftCallback).
        if (error instanceof SystemError && error.statusCode === 409) {
          await this.fetchActiveAgreement()
        }
        throw error
      }
      if (this.agreement) {
        this.agreement = { ...this.agreement, accepted: true }
      }
    }
  }
})
